import { useState } from 'react';
import ToggleButton from './ToggleButton';
import EquipmentPhotoSection from './EquipmentPhotoSection';

const FLUSH_TYPES = [
    { value: 'dual_flush',          label: 'Double chasse' },
    { value: 'simple_flush',        label: 'Chasse simple' },
    { value: 'interruptible_flush', label: 'Interrompable' },
];

const DUAL_PRESETS = [
    { value: '6/9',    label: '6 / 9 L',    hint: 'Classique', min: 6, max: 9 },
    { value: '3/6',    label: '3 / 6 L',    hint: 'Moderne',   min: 3, max: 6 },
    { value: 'custom', label: 'Personnalisé', hint: null,        min: null, max: null },
];

function initPreset(data) {
    if (data.flush_volume_min === 6 && data.flush_volume_max === 9) return '6/9';
    if (data.flush_volume_min === 3 && data.flush_volume_max === 6) return '3/6';
    return 'custom';
}

function WCFields({ data, onChange, imagePreview, onImageChange, onDeleteImage, photoDisabled }) {
    const [showMore, setShowMore] = useState(false);
    const [volumePreset, setVolumePreset] = useState(() => initPreset(data));

    const handleToggle = (name) => (e) => onChange({ target: { name, value: e.target.checked } });

    const handlePreset = (p) => {
        setVolumePreset(p.value);
        if (p.min !== null) {
            onChange({ target: { name: 'flush_volume_min', value: p.min } });
            onChange({ target: { name: 'flush_volume_max', value: p.max } });
        }
    };

    return (
        <div className="equip-fields">

            {/* Nombre */}
            <div className="equip-field">
                <span className="equip-field-label">Nombre d'équipements</span>
                <input type="number" name="nb_equipment" value={data.nb_equipment ?? ''} onChange={onChange} min={1} className="equip-field-num" />
            </div>

            {/* Type de chasse */}
            <div className="equip-field equip-field--col">
                <span className="equip-field-label">Type de chasse</span>
                <div className="equip-mode-pills">
                    {FLUSH_TYPES.map(t => (
                        <button
                            key={t.value}
                            type="button"
                            className={`equip-mode-pill${data.flush_type === t.value ? ' active' : ''}`}
                            onClick={() => onChange({ target: { name: 'flush_type', value: t.value } })}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Volume — double chasse */}
            {data.flush_type === 'dual_flush' && (
                <div className="equip-field equip-field--col">
                    <span className="equip-field-label">Volume de chasse</span>
                    <div className="equip-mode-pills">
                        {DUAL_PRESETS.map(p => (
                            <button
                                key={p.value}
                                type="button"
                                className={`equip-mode-pill${volumePreset === p.value ? ' active' : ''}`}
                                onClick={() => handlePreset(p)}
                            >
                                {p.hint ? `${p.label} (${p.hint})` : p.label}
                            </button>
                        ))}
                    </div>
                    {volumePreset === 'custom' && (
                        <div className="equip-wc-volumes">
                            <div className="equip-field equip-field--last">
                                <span className="equip-field-label">Grande chasse</span>
                                <div className="equip-field-compound">
                                    <input type="number" name="flush_volume_max" value={data.flush_volume_max ?? ''} onChange={onChange} min={1} className="equip-field-num" />
                                    <span className="equip-field-unit-badge">L</span>
                                </div>
                            </div>
                            <div className="equip-field equip-field--last">
                                <span className="equip-field-label">Petite chasse</span>
                                <div className="equip-field-compound">
                                    <input type="number" name="flush_volume_min" value={data.flush_volume_min ?? ''} onChange={onChange} min={0} className="equip-field-num" />
                                    <span className="equip-field-unit-badge">L</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Volume — chasse simple */}
            {data.flush_type === 'simple_flush' && (
                <div className="equip-field">
                    <span className="equip-field-label">Volume de la chasse</span>
                    <div className="equip-field-compound">
                        <input type="number" name="flush_volume_max" value={data.flush_volume_max ?? ''} onChange={onChange} min={1} className="equip-field-num" />
                        <span className="equip-field-unit-badge">L</span>
                    </div>
                </div>
            )}

            {/* Débit — chasse interrompable */}
            {data.flush_type === 'interruptible_flush' && (
                <div className="equip-field">
                    <span className="equip-field-label">Débit de la chasse</span>
                    <div className="equip-field-compound">
                        <input type="number" name="flush_flow" value={data.flush_flow ?? ''} onChange={onChange} min={0.1} className="equip-field-num" />
                        <span className="equip-field-unit-badge">L/min</span>
                    </div>
                </div>
            )}

            {/* Petit bouton de chasse */}
            {data.flush_type === 'dual_flush' && (
                <div className="equip-field">
                    <span className="equip-field-label">Petit bouton de chasse</span>
                    <ToggleButton name="is_small_flush_button" value={!!data.is_small_flush_button} onChange={handleToggle('is_small_flush_button')} context="equipment" />
                </div>
            )}

            {/* Toilette encastrée */}
            <div className={`equip-toggle-card${data.built_in ? ' equip-toggle-card--on' : ''}`}>
                <div className="equip-toggle-card-body">
                    <span className="equip-toggle-card-label">Toilette encastrée</span>
                    <span className="equip-toggle-card-hint">Réservoir intégré dans le mur</span>
                </div>
                <ToggleButton name="built_in" value={!!data.built_in} onChange={handleToggle('built_in')} context="equipment" />
            </div>

            {/* Photo */}
            <EquipmentPhotoSection imagePreview={imagePreview} onImageChange={onImageChange} onDeleteImage={onDeleteImage} disabled={photoDisabled} />

            {/* Commentaire */}
            <div className="equip-field equip-field--col">
                <span className="equip-field-label">Commentaire</span>
                <input type="text" name="general_comment" value={data.general_comment || ''} onChange={onChange} placeholder="Commentaire général" />
            </div>

            {/* Autres informations */}
            <button type="button" className={`equip-more-btn${showMore ? ' equip-more-btn--open' : ''}`} onClick={() => setShowMore(p => !p)}>
                <span>Autres informations</span>
                <svg className="equip-more-chevron" width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            </button>

            {showMore && (
                <div className="equip-more-content">
                    <div className="equip-field">
                        <span className="equip-field-label">Chasse à bascule possible</span>
                        <ToggleButton name="bascule_flush_possible" value={!!data.bascule_flush_possible} onChange={handleToggle('bascule_flush_possible')} context="equipment" />
                    </div>
                    <div className="equip-field">
                        <span className="equip-field-label">Réservoir d'eau</span>
                        <ToggleButton name="is_water_tank" value={!!data.is_water_tank} onChange={handleToggle('is_water_tank')} context="equipment" />
                    </div>
                    <div className="equip-field">
                        <span className="equip-field-label">Système de réduction de consommation</span>
                        <ToggleButton name="consumption_reduction_system" value={!!data.consumption_reduction_system} onChange={handleToggle('consumption_reduction_system')} context="equipment" />
                    </div>
                    <div className="equip-field">
                        <span className="equip-field-label">Vanne d'isolement</span>
                        <ToggleButton name="isolation_valve" value={!!data.isolation_valve} onChange={handleToggle('isolation_valve')} context="equipment" />
                    </div>
                    <div className="equip-field equip-field--col">
                        <span className="equip-field-label">Qualité de l'eau en entrée</span>
                        <select name="water_quality_in" value={data.water_quality_in || 'drinkable'} onChange={onChange}>
                            <option value="drinkable">Eau potable</option>
                            <option value="non_drinkable">Eau non potable</option>
                        </select>
                    </div>
                    <div className="equip-field equip-field--col equip-field--last">
                        <span className="equip-field-label">Référence</span>
                        <input type="text" name="reference" value={data.reference || ''} onChange={onChange} placeholder="Référence ou modèle" />
                    </div>
                </div>
            )}
        </div>
    );
}

export default WCFields;
